import React, { useState } from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  IconButton,
  Popover,
  SxProps,
  TableCell,
  TableSortLabel,
  TextField,
  Theme,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';

export type SortDirection = 'asc' | 'desc';

/**
 * Shared sortable + filterable `<TableCell>` for the admin catalog list
 * screens (worker brief "catalog-list-ui-pattern", 2026-09-05). Presentational
 * only - the caller owns all sort/filter state and just wires callbacks in.
 *
 * - Sorting: pass `onSort` to make the label clickable (MUI `TableSortLabel`);
 *   omit it to render a plain, non-sortable header cell. The sort arrow is
 *   always rendered next to the label (worker brief
 *   "catalog-list-filter-polish", 2026-09-06) - dimmed/neutral when this
 *   column isn't the active sort, full-strength and pointing `sortDirection`
 *   when it is, so the affordance is discoverable without hovering.
 * - Filtering: pass a non-empty `filterOptions` (+ `onFilterChange`) to show a
 *   small funnel icon that opens a popover with a multi-select Autocomplete
 *   (worker brief "catalog-list-filter-polish") listing the column's distinct
 *   values, OR-combined - see `getDistinctValues` below to compute that list
 *   client-side from whatever row set is already loaded (no backend endpoint
 *   needed). Omit `filterOptions` to render no filter affordance.
 */
export interface SortableFilterableHeaderProps {
  label: string;
  align?: 'left' | 'center' | 'right';
  sx?: SxProps<Theme>;
  /** Whether THIS column is the currently active sort column. */
  sortActive?: boolean;
  /** Direction to display when `sortActive` is true. Defaults to 'asc'. */
  sortDirection?: SortDirection;
  /** Omit to make this column non-sortable. */
  onSort?: () => void;
  /** Omit or pass an empty array to make this column non-filterable. */
  filterOptions?: string[];
  /** Currently selected filter values for this column (OR-combined). Empty/undefined = unfiltered. */
  filterValue?: string[];
  onFilterChange?: (values: string[]) => void;
  filterLabel?: string;
}

export const SortableFilterableHeader: React.FC<SortableFilterableHeaderProps> = ({
  label,
  align = 'center',
  sx,
  sortActive,
  sortDirection = 'asc',
  onSort,
  filterOptions,
  filterValue,
  onFilterChange,
  filterLabel,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const filterable = !!filterOptions && filterOptions.length > 0 && !!onFilterChange;
  const selectedValues = filterValue || [];
  const hasActiveFilter = selectedValues.length > 0;

  return (
    <TableCell
      align={align}
      sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', ...sx }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent={align === 'left' ? 'flex-start' : 'center'}
        gap={0.25}
      >
        {onSort ? (
          <TableSortLabel
            active={!!sortActive}
            direction={sortActive ? sortDirection : 'asc'}
            onClick={onSort}
            sx={{
              color: 'inherit',
              '&.Mui-active': { color: 'inherit' },
              // Always-visible sort arrow (worker brief
              // "catalog-list-filter-polish", 2026-09-06): MUI's default
              // hides `.MuiTableSortLabel-icon` (opacity 0) unless this
              // column is active or hovered - override to keep it visible on
              // every sortable column, just dimmed/neutral when inactive so
              // the active column's direction still reads as clearly
              // distinct.
              '& .MuiTableSortLabel-icon': {
                color: 'inherit !important',
                opacity: sortActive ? '1 !important' : '0.5 !important',
              },
            }}
          >
            {label}
          </TableSortLabel>
        ) : (
          label
        )}
        {filterable && (
          <>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ color: 'inherit', p: 0.25 }}
              title={`Filtrar por ${filterLabel || label}`}
            >
              <Badge color="warning" variant="dot" invisible={!hasActiveFilter}>
                <FilterList fontSize="small" />
              </Badge>
            </IconButton>
            <Popover
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 1.5, minWidth: 220 }}>
                <Autocomplete
                  multiple
                  size="small"
                  openOnFocus
                  disableCloseOnSelect
                  options={filterOptions || []}
                  value={selectedValues}
                  onChange={(_e, value) => {
                    onFilterChange?.(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Filtrar ${filterLabel || label}`}
                      autoFocus
                      helperText="Varios valores = OR"
                    />
                  )}
                />
              </Box>
            </Popover>
          </>
        )}
      </Box>
    </TableCell>
  );
};

export default SortableFilterableHeader;

/**
 * Distinct, non-empty, alphanumerically-sorted string values for one column
 * of an already-loaded row set - the client-side data source every filter
 * dropdown above needs (no new backend endpoint: these catalog list pages
 * already fetch their full dataset up front and paginate client-side).
 */
export function getDistinctValues<T>(rows: T[], key: keyof T): string[] {
  const values = new Set<string>();
  rows.forEach((row) => {
    const v = row[key];
    if (v !== null && v !== undefined && String(v).trim() !== '') {
      values.add(String(v));
    }
  });
  return Array.from(values).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  );
}

/**
 * The inline-edit-row counterpart to the header filter above: a `freeSolo`
 * MUI Autocomplete offering a column's existing distinct values, so an admin
 * editing a row can pick one of them OR type a genuinely new value (typing
 * doesn't get blocked/validated against the list - `freeSolo` is exactly
 * "suggest, don't constrain"). Presentational, same `size`/`variant` as the
 * plain `TextField`s it replaces so it drops into an existing inline-edit
 * row without changing row height/layout.
 */
export interface FreeSoloAutocompleteFieldProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  label?: string;
  sx?: SxProps<Theme>;
}

export const FreeSoloAutocompleteField: React.FC<FreeSoloAutocompleteFieldProps> = ({
  value,
  options,
  onChange,
  label,
  sx,
}) => (
  <Autocomplete
    freeSolo
    size="small"
    options={options}
    value={value}
    inputValue={value}
    onChange={(_e, newValue) => onChange(newValue || '')}
    onInputChange={(_e, newInputValue) => onChange(newInputValue)}
    renderInput={(params) => <TextField {...params} variant="standard" label={label} />}
    sx={sx}
  />
);
